import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { keys } from '@/api/keys'
import type { TimelineKind } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useCurrentUser, VerifyEmailBanner } from '@/features/auth'
import { Composer, PostList } from '@/features/posts'
import {
  KindFilter,
  NewPostsBanner,
  useForYouTimeline,
  useHomeTimeline,
  useNewPostCount,
} from '@/features/timeline'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

export function HomePage() {
  const t = useT()
  const { user } = useCurrentUser()
  const client = useQueryClient()
  const [tab, setTab] = useState<'following' | 'foryou'>('following')
  const [kind, setKind] = useState<TimelineKind>('all')

  const timeline = useHomeTimeline(tab === 'following', kind)
  // 为你推荐只在切过去时才去排：排序比关注流贵得多
  const forYou = useForYouTimeline(tab === 'foryou', kind)
  const firstPostId = timeline.data?.pages[0]?.items[0]?.id
  const { data: newCount = 0 } = useNewPostCount(firstPostId, Boolean(user))

  const loadNewPosts = () => {
    client.invalidateQueries({ queryKey: keys.timelineHome(kind) })
    client.invalidateQueries({ queryKey: ['timeline', 'home', 'new-count'] })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      <PageHeader title={t('nav.home')}>
        <div className="flex border-b border-line" role="tablist">
          {(
            [
              ['following', 'home.following'],
              ['foryou', 'home.forYou'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                'relative flex-1 py-4 text-[15px] transition-colors hover:bg-surface-hover',
                tab === key ? 'font-bold text-ink' : 'text-ink-muted',
              )}
            >
              {t(label)}
              {tab === key && (
                <span className="absolute inset-x-0 bottom-0 mx-auto h-1 w-14 bg-brand-ink" />
              )}
            </button>
          ))}
        </div>
      </PageHeader>

      <VerifyEmailBanner />

      {tab === 'foryou' ? (
        <>
          <div className="border-b border-line pt-2">
            <KindFilter value={kind} onChange={setKind} />
          </div>
          <PostList
            query={forYou}
            emptyTitle={t('home.forYouEmptyTitle')}
            emptyDescription={t('home.forYouEmptyBody')}
          />
        </>
      ) : (
        <>
          <div className="border-b border-line">
            <Composer />
          </div>

          <div className="border-b border-line pt-2">
            <KindFilter value={kind} onChange={setKind} />
          </div>

          <NewPostsBanner count={newCount} onClick={loadNewPosts} />

          <PostList
            query={timeline}
            emptyTitle={t('home.emptyTitle')}
            emptyDescription={t('home.emptyBody')}
          />
        </>
      )}
    </>
  )
}
